import { useState, useRef, useEffect } from 'react';
import { PenTool, Check, X, AlertCircle, Shield, CheckCircle2 } from 'lucide-react';

/**
 * Enhanced Signature Capture Component (Frontend)
 * 
 * UETA/E-SIGN compliant multi-step signature workflow for email-link signers:
 * Step 1: Display & acknowledge legal agreement
 * Step 2: Draw signature on canvas
 * Step 3: Capture metadata & verify signature
 * 
 * Used by: ClientFinalSignatureView & email-link signing flows
 */
export default function EnhancedSignatureCapture({
  onSignatureComplete,
  onCancel,
  signerInfo = {},
  documentTitle = 'Document',
  requiresMultipleSigners = false,
  signerSequence = 1,
  totalSigners = 1,
  isLoading = false,
}) {
  // Step control
  const [step, setStep] = useState(1);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Signature drawing
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);

  // Metadata
  const [metadata, setMetadata] = useState(null);

  // Collect metadata on mount
  useEffect(() => {
    const collectMetadata = async () => {
      const now = new Date();
      const metadata = {
        timestamp: now.toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        userAgent: navigator.userAgent,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        platform: navigator.platform,
        timestamp_unix: now.getTime(),
      };

      // Get IP address from API (optional, backend will add it)
      try {
        const response = await fetch('https://api.ipify.org?format=json');
        const data = await response.json();
        metadata.ipAddress = data.ip;
      } catch (e) {
        // IP collection failed, backend will add it
        metadata.ipAddress = 'pending_from_backend';
      }

      setMetadata(metadata);
    };

    collectMetadata();
  }, []);

  // Helper to get coordinates from mouse or touch events
  const getCoordinates = (e) => {
    if (!canvasRef.current) return null;
    const rect = canvasRef.current.getBoundingClientRect();
    
    let clientX, clientY;
    if (e.touches) {
      // Touch event
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      // Mouse event
      clientX = e.clientX;
      clientY = e.clientY;
    }
    
    // Account for canvas scaling on mobile
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;
    
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  // Canvas drawing handlers - supports both mouse and touch
  const startDrawing = (e) => {
    if (!canvasRef.current) return;
    e.preventDefault();
    const coords = getCoordinates(e);
    if (!coords) return;
    
    setIsDrawing(true);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
  };

  const draw = (e) => {
    if (!isDrawing || !canvasRef.current) return;
    e.preventDefault();
    const coords = getCoordinates(e);
    if (!coords) return;
    
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1f2937';
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
    setIsEmpty(false);
  };

  const stopDrawing = (e) => {
    if (e) e.preventDefault();
    setIsDrawing(false);
  };

  const clearSignature = () => {
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      setIsEmpty(true);
    }
  };

  // Generate SHA-256 hash using SubtleCrypto
  const generateSignatureHash = async (imageData) => {
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(imageData);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
      return hashHex;
    } catch (e) {
      console.error('SHA-256 generation failed:', e);
      return null;
    }
  };

  // Handle signature completion
  const handleSignatureComplete = async () => {
    if (isEmpty) {
      alert('Please sign before continuing');
      return;
    }

    if (!canvasRef.current) return;

    const signatureImage = canvasRef.current.toDataURL('image/png');
    const signatureHash = await generateSignatureHash(signatureImage);

    // Prepare signature data
    const signatureData = {
      imageData: signatureImage.split(',')[1], // Base64 only
      hash: signatureHash,
      legalAgreement: {
        version: '1.0',
        agreedAt: new Date().toISOString(),
        agreedToTerms: true,
      },
    };

    // Call parent handler
    await onSignatureComplete({
      signatureData,
      signerName: signerInfo.name || 'Unknown Signer',
      signerEmail: signerInfo.email || '',
      metadata,
    });
  };

  // Step 1: Legal Agreement
  if (step === 1) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start gap-4">
          <div className="p-3 bg-blue-100 rounded-lg">
            <Shield size={24} className="text-blue-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-secondary-900">
              Legal Agreement & Signature Acknowledgment
            </h2>
            <p className="text-sm text-secondary-600 mt-1">
              Document: <span className="font-medium">{documentTitle}</span>
            </p>
          </div>
        </div>

        {/* Multi-Signer Context */}
        {requiresMultipleSigners && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-900">
              <span className="font-semibold">Signer {signerSequence} of {totalSigners}</span> in this approval workflow
            </p>
          </div>
        )}

        {/* Legal Agreement Text */}
        <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-6 space-y-4 max-h-96 overflow-y-auto">
          <h3 className="font-semibold text-secondary-900">Electronic Signature Agreement</h3>

          <div className="text-sm text-secondary-700 space-y-3">
            <p>
              <strong>1. Scope of Agreement</strong>
              <br />
              By clicking "I Agree" below, you consent to sign this document electronically. This digital signature is
              intended to have the same legal effect and enforceability as a handwritten signature under the Uniform
              Electronic Transactions Act (UETA) and the Electronic Signatures in Global and National Commerce Act
              (E-SIGN).
            </p>

            <p>
              <strong>2. Signer Intent & Authorization</strong>
              <br />
              You certify that you are the person identified above and are authorized to sign this document. By
              signing, you acknowledge that you have reviewed the document in full and approve it for processing.
            </p>

            <p>
              <strong>3. Technical Requirements</strong>
              <br />
              You represent that you have the technical capability to receive, review, and retain electronic records
              and that your electronic signature will be retained and remains accessible for later reference and use.
            </p>

            <p>
              <strong>4. Record Retention</strong>
              <br />
              This organization will maintain a complete and accurate record of your signature, including metadata such
              as date, time, IP address, and device information for audit and compliance purposes.
            </p>

            <p>
              <strong>5. No Waiver of Rights</strong>
              <br />
              You may request that we provide a copy of this electronically signed document in a permanent fixed format
              (such as PDF).
            </p>

            <p>
              <strong>6. Acknowledgment</strong>
              <br />
              You acknowledge that you have read this agreement, understand its terms, and agree to be bound by your
              electronic signature.
            </p>
          </div>
        </div>

        {/* Checkbox */}
        <label className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors">
          <input
            type="checkbox"
            checked={agreedToTerms}
            onChange={(e) => setAgreedToTerms(e.target.checked)}
            className="mt-1"
          />
          <span className="text-sm text-secondary-900">
            I have read and agree to the electronic signature agreement above. I understand that my electronic
            signature is legally binding.
          </span>
        </label>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button onClick={onCancel} className="btn-secondary flex-1 flex items-center justify-center gap-2">
            <X size={16} />
            Cancel
          </button>
          <button
            onClick={() => setStep(2)}
            disabled={!agreedToTerms}
            className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Check size={16} />
            I Agree & Continue
          </button>
        </div>
      </div>
    );
  }

  // Step 2: Signature Capture
  if (step === 2) {
    return (
      <div className="space-y-6">
        {/* Progress */}
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-100 text-green-700 font-semibold text-sm">
            <CheckCircle2 size={20} />
          </div>
          <div className="flex-1 h-1 bg-secondary-200 rounded-full"></div>
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-semibold text-sm">
            2
          </div>
        </div>

        {/* Header */}
        <div className="flex items-start gap-4">
          <div className="p-3 bg-blue-100 rounded-lg">
            <PenTool size={24} className="text-blue-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-secondary-900">Draw Your Signature</h2>
            <p className="text-sm text-secondary-600 mt-1">
              Sign in the box below using your mouse or touchpad
            </p>
          </div>
        </div>

        {/* Signer Info */}
        <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-4">
          <p className="text-sm">
            <span className="font-semibold text-secondary-900">Signing as:</span>
            <br />
            <span className="text-secondary-700">
              {signerInfo.name || 'Unknown'} {signerInfo.email ? `(${signerInfo.email})` : ''}
            </span>
          </p>
        </div>

        {/* Canvas */}
        <div className="border-2 border-secondary-300 rounded-lg bg-white overflow-hidden">
          <canvas
            ref={canvasRef}
            width={500}
            height={250}
            className="w-full h-auto block bg-white cursor-crosshair"
            style={{ 
              touchAction: 'none',
              WebkitTouchCallout: 'none',
              userSelect: 'none',
              WebkitUserSelect: 'none'
            }}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
            onTouchCancel={stopDrawing}
          />
        </div>

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
          <AlertCircle size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-900">
            Sign naturally using your mouse, touchpad, or finger. Avoid lifting between strokes. Your signature will be digitally verified (SHA-256).
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => setStep(1)}
            disabled={isLoading}
            className="btn-secondary flex items-center justify-center gap-2 py-3 sm:py-2"
          >
            <X size={16} />
            Back
          </button>
          <button
            onClick={clearSignature}
            disabled={isEmpty || isLoading}
            className="btn-secondary flex-1 flex items-center justify-center gap-2 py-3 sm:py-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Clear
          </button>
          <button
            onClick={handleSignatureComplete}
            disabled={isEmpty || isLoading}
            className="btn-primary flex-1 flex items-center justify-center gap-2 py-3 sm:py-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <span className="animate-spin">⏳</span>
                Verifying...
              </>
            ) : (
              <>
                <Check size={16} />
                Complete Signature
              </>
            )}
          </button>
        </div>
      </div>
    );
  }
}
